import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { SplitText } from "gsap/SplitText"
import { module as BaseModule } from "modujs"

gsap.registerPlugin(SplitText, ScrollTrigger)

export default class extends BaseModule {
  init() {
    const { effect } = this.el.dataset
    this[effect]()
  }

  separator() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        gsap.fromTo(this.el, {
          opacity: 0,
          width: 0,
        }, {
          opacity: 1,
          duration: 1,
          width: "100%",
          ease: "power2.out",
        })
      }
    })
  }

  verticalSeparator() {
    const delay = this.el.dataset.effectDelay ? parseFloat(this.el.dataset.effectDelay) : 0.3
    const duration = this.el.dataset.effectDuration ? parseFloat(this.el.dataset.effectDuration) : 1

    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        gsap.fromTo(this.el, {
          opacity: 0,
          scaleY: 0,
          transformOrigin: "top center",
        }, {
          opacity: 1,
          duration,
          delay,
          scaleY: 1,
          ease: "power2.out",
        })
      }
    })
  }

  fade() {
  const delay = this.el.dataset.effectDelay ? parseFloat(this.el.dataset.effectDelay) : 0.3
  const duration = this.el.dataset.effectDuration ? parseFloat(this.el.dataset.effectDuration) : 1

    gsap.fromTo(this.el, {
      opacity: 0,
    }, {
      opacity: 1,
      delay,
      duration,
    })
  }

  rotate() {
    gsap.fromTo(this.el, {
      rotation: 0,
      opacity: 0,
    }, {
      rotation: 360,
      opacity: 1,
      duration: 2,
      ease: "power2.out",
    })
  }

  delayedFade() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        this.fade()
      }
    })
  }

  delayedSplit() {
    ScrollTrigger.create({
      trigger: this.el,
      start: "top bottom",
      end: "bottom top",
      once: true,
      onEnter: () => {
        this.split()
      }
    })
  }

  split() {
    const delay = this.el.dataset.effectDelay ? parseFloat(this.el.dataset.effectDelay) : 0.3
    const duration = this.el.dataset.effectDuration ? parseFloat(this.el.dataset.effectDuration) : 1.5

    document.fonts.ready.then(() => {
      gsap.set(this.el, { opacity: 1 })

      let split
      SplitText.create(this.el, {
        type: "words,lines",
        linesClass: "line",
        autoSplit: true,
        mask: "lines",
        onSplit: (self) => {
          split = gsap.from(self.lines, {
            duration,
            delay,
            yPercent: 100,
            opacity: 0,
            stagger: 0.2,
            ease: "expo.out",
          })
          return split
        },
      })
    })
  }

  footer() {
    if (window.innerWidth < 1024) return;

    const form = this.el.querySelector("[data-footer-email-input]")
    const consent = this.el.querySelector("[data-footer-consent]")
    const endLeft = this.el.querySelector("[data-footer-end-left]")
    const endRight = this.el.querySelector("[data-footer-end-right]")
    const menuLeft = this.el.querySelector("[data-footer-menu-left]")
    const menuRight = this.el.querySelector("[data-footer-menu-right]")
    const separator = this.el.querySelector("[data-footer-separator]")

    const animation = gsap.timeline({ paused: true })
      .fromTo(form, { opacity: 0, width: '240px' },
         { opacity: 1, duration: 3, width: '100%' })
      .to(consent, { opacity: 1, duration: 1 }, "-=1")
      .to(menuLeft, { opacity: 1, duration: 1.5 }, "-=0.5")
      .fromTo(separator, { height: 0 }, 
        { opacity: 1, duration: 1.5, height: '100%' }, "<")
      .to(menuRight, { opacity: 1, duration: 1.5 }, "<0.5")
      .to(endLeft, { opacity: 1, duration: 1 })
      .to(endRight, { opacity: 1, duration: 1 })

    let scrollTrigger = null

    const setupAnimation = () => {
      if (window.innerWidth >= 1024) {
        if (!scrollTrigger) {
          scrollTrigger = ScrollTrigger.create({
            animation,
            trigger: this.el,
            start: "60% 100%",
            end: "max",
            scrub: 2,
          })
        } else {
          scrollTrigger.enable()
        }
      } else {
        if (scrollTrigger) {
          scrollTrigger.disable()
          animation.progress(0)
          // Clear inline styles that GSAP added
          gsap.set([form, consent, endLeft, endRight, menuLeft, menuRight, separator], {
            clearProps: "all"
          })
        }
      }
    }

    // Initial setup
    setupAnimation()

    // Handle resize
    let resizeTimeout
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        setupAnimation()
        ScrollTrigger.refresh()
      }, 100)
    })
  }
}
